import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import { LIVE_PAINTING as L } from "../data/livePainting";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LivePainting() {
  return (
    <div data-testid="live-painting-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
        <header className="lg:col-span-7">
          <p className="label mb-4">{L.title}</p>
          <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
            {L.hero.headline} <span className="italic text-[hsl(var(--ink-soft))]">{L.hero.headlineItalic}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">{L.hero.sub}</p>
          <a
            href="#book"
            className="mt-10 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
            data-testid="live-hero-cta"
          >
            Request a date <ArrowDown className="h-3.5 w-3.5" />
          </a>
        </header>
        <dl className="lg:col-span-5 border-t border-border" data-testid="live-facts">
          {L.facts.map((f) => (
            <div key={f.k} className="grid grid-cols-[90px_1fr] gap-4 py-3.5 border-b border-border">
              <dt className="label text-[hsl(var(--ink-soft))]">{f.k}</dt>
              <dd className="text-base">{f.v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="border-t border-border bg-[hsl(var(--surface))]" data-testid="live-how">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-12 md:mb-16 max-w-2xl">
            <p className="label mb-3">How it works</p>
            <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
              From first email <span className="italic text-[hsl(var(--ink-soft))]">to a painting on your wall.</span>
            </h2>
          </header>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {L.how.map((s) => (
              <li key={s.n} className="bg-background p-7 md:p-8 min-h-[220px] flex flex-col justify-between">
                <span className="serif text-3xl text-[hsl(var(--accent))]">{s.n}</span>
                <div>
                  <p className="serif text-2xl mt-1 leading-tight">{s.title}</p>
                  <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t border-border bg-foreground text-background" data-testid="live-included">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <figure className="lg:col-span-7">
            <div className="aspect-[3/2] overflow-hidden bg-background/10">
              <img src={L.photo.src} alt={L.photo.alt} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <figcaption className="mt-3 label text-background/50">{L.photo.caption}</figcaption>
          </figure>
          <div className="lg:col-span-5">
            <p className="label mb-3 text-background/60">What's included</p>
            <ul className="border-t border-background/20">
              {L.included.map((w) => (
                <li key={w} className="flex items-start gap-4 py-4 border-b border-background/20">
                  <Check className="h-4 w-4 mt-1.5 shrink-0 text-[hsl(var(--accent))]" />
                  <span className="serif text-xl md:text-2xl leading-snug">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-border" data-testid="live-faq">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <p className="label mb-3">Good to know</p>
            <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
              Questions <span className="italic text-[hsl(var(--ink-soft))]">planners ask.</span>
            </h2>
          </div>
          <dl className="md:col-span-8 border-t border-border">
            {L.faq.map((f) => (
              <div key={f.q} className="py-5 border-b border-border">
                <dt className="serif text-xl md:text-2xl">{f.q}</dt>
                <dd className="mt-2 text-sm md:text-base text-[hsl(var(--ink-soft))] leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <BookingForm />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label mb-2 block">{label}</span>
      {children}
    </label>
  );
}

const input =
  "w-full bg-transparent border-b border-border py-2.5 text-base outline-none focus:border-foreground transition-colors";

const initial = { name: "", email: "", phone: "", event_type: "", event_date: "", venue: "", guest_count: "", duration: "", canvas_size: "", notes: "" };

function BookingForm() {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const valid = f.name.trim() && /.+@.+\..+/.test(f.email) && f.event_type && f.event_date;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await axios.post(`${API}/live-painting`, {
        ...f,
        phone: f.phone || null,
        venue: f.venue || null,
        guest_count: f.guest_count ? Number(f.guest_count) : null,
        duration: f.duration || null,
        canvas_size: f.canvas_size || null,
        notes: f.notes || null,
      });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please email hello@josephgravesjr.com.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="book" className="border-t border-border bg-[hsl(var(--surface))] scroll-mt-24" data-testid="live-book">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="label mb-3">Request a date</p>
          <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
            Tell us about <span className="italic text-[hsl(var(--ink-soft))]">the moment.</span>
          </h2>
          <p className="mt-5 text-[hsl(var(--ink-soft))] leading-relaxed">
            No payment now. Joseph will reply within two business days with availability and a quote. A 25% deposit holds the date once you're ready.
          </p>
        </div>
        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="live-book-success">
              <div className="inline-flex items-center gap-2 mb-4 text-[hsl(var(--accent))]">
                <Check className="h-4 w-4" />
                <span className="label !text-[hsl(var(--accent))]">Request received</span>
              </div>
              <p className="serif text-3xl md:text-4xl tracking-tight font-light leading-tight">
                Thank you. Joseph will be in touch with a quote and availability for {f.event_date}.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-8" noValidate data-testid="live-book-form">
              <Field label="Your name">
                <input required value={f.name} onChange={set("name")} className={input} autoComplete="name" data-testid="live-name" />
              </Field>
              <Field label="Email">
                <input required type="email" value={f.email} onChange={set("email")} className={input} autoComplete="email" data-testid="live-email" />
              </Field>
              <Field label="Phone">
                <input type="tel" value={f.phone} onChange={set("phone")} className={input} autoComplete="tel" data-testid="live-phone" />
              </Field>
              <Field label="Event type">
                <select required value={f.event_type} onChange={set("event_type")} className={input} data-testid="live-event-type">
                  <option value="">Select…</option>
                  {L.form.eventTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Event date">
                <input required type="date" value={f.event_date} onChange={set("event_date")} className={input} data-testid="live-event-date" />
              </Field>
              <Field label="Venue / city">
                <input value={f.venue} onChange={set("venue")} className={input} placeholder="Venue name, Denver" data-testid="live-venue" />
              </Field>
              <Field label="Approx. guests">
                <input type="number" min="1" value={f.guest_count} onChange={set("guest_count")} className={input} data-testid="live-guests" />
              </Field>
              <Field label="How long on-site?">
                <select value={f.duration} onChange={set("duration")} className={input} data-testid="live-duration">
                  <option value="">Select…</option>
                  {L.form.durations.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </Field>
              <Field label="Canvas size">
                <select value={f.canvas_size} onChange={set("canvas_size")} className={input} data-testid="live-canvas">
                  <option value="">Select…</option>
                  {L.form.canvasSizes.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="The moment you'd like painted" className="md:col-span-2">
                <textarea rows={3} value={f.notes} onChange={set("notes")} className={`${input} resize-none`} placeholder="First dance at 8pm, the ceremony arch, the opening-night crowd…" data-testid="live-notes" />
              </Field>
              <div className="md:col-span-2 flex items-center justify-between gap-6 pt-2">
                <p className="text-xs text-[hsl(var(--ink-soft))] max-w-sm">Used only to reply about your event. Never sold or shared.</p>
                <button
                  type="submit"
                  disabled={!valid || busy}
                  className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-40 transition-opacity whitespace-nowrap"
                  data-testid="live-book-submit"
                >
                  {busy ? "Sending…" : "Request a quote"}
                  {!busy && <ArrowRight className="h-3.5 w-3.5" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
